<template>
	<div class="twozero">
		<div
			id="hideinmobile"
			class="d-flex w-100 justify-content-center align-items-center gap-4 mt-4"
		>
			<h1 id="title" class="mr-4">2048</h1>
			<article>
				<h5>Current score</h5>
				<h4>{{ score }}</h4>
			</article>
			<article>
				<h5>Best Score</h5>
				<h4>{{ bestTry }}</h4>
			</article>
		</div>

		<div
			id="hideinlaptop"
			class="d-flex w-100 justify-content-center align-items-center gap-4 mt-4"
		>
			<article>
				<h5>Current score</h5>
				<h4>{{ score }}</h4>
			</article>
			<article>
				<h5>Best try</h5>
				<h4>#{{ bestTry }}</h4>
			</article>
		</div>

		<!-- board container (we manage tile DOM inside JS) -->
		<div
			@touchstart="handleTouchStart"
			@touchend="handleTouchEnd"
			id="boards"
			class="position-relative"
			ref="boards"
		></div>

		<div class="reset-twozero-hover" @click="showReset = true">
			<img src="../../assets/newdesign/dashboard/games/reset-game.svg" alt="" />
			<span class="pr-3 ml-2">Reset game</span>
		</div>

		<b-button
			class="pg-addBtn two-zero-btn-padding twozero-margin"
			@click="undoMove"
			:disabled="!canUndo || undoCount >= 5"
		>
			Undo move
		</b-button>

		<ResetModal
			v-if="showReset"
			:showReset="showReset"
			@close-modal="showReset = false"
			@resetGame="resetGame"
		/>
	</div>
</template>

<script>
import ResetModal from "./ResetModal.vue";
import gamesScore from "@/components/mixin/gamesScore";
import { getGameState, getMyRank, saveActivity } from "@/utils/apiFunctions";
import Loader from "@/components/fixedcomponents/Loader.vue";

export default {
	mixins: [gamesScore],
	components: { Loader, ResetModal },
	data() {
		return {
			// game state
			board: Array(4)
				.fill(null)
				.map(() => Array(4).fill(0)),
			score: 0,
			rows: 4,
			columns: 4,
			gameOver: false,
			highest: 0,
			myScore: 0,
			bestTry: 0,
			totalPlayed: 0,
			boardHistory: [],
			scoreHistory: [],
			highestHistory: [],
			canUndo: false,
			undoCount: 0,
			showReset: false,
			buttonLoader: false,

			// animation/layout
			isMoving: false,
			animDuration: 150, // ms
			boardPadding: 18, // matches your CSS
			cellWidth: 87,
			cellHeight: 82,
			gap: 18,
			activityInterval: null,
		};
	},
	mounted() {
		this.startActivityTimer();
		this.getMyRank();
		this.initializeBoard(); // create tile DOM nodes
		this.loadGameState();

		this.$nextTick(() => {
			this.computeLayoutMetrics();
			window.addEventListener("resize", this.computeLayoutMetrics);
		});

		window.addEventListener("keyup", this.handleKeyup);
		const boardElement = this.$refs.boards;
		if (boardElement) {
			boardElement.addEventListener("touchstart", this.handleTouchStart, {
				passive: false,
			});
			boardElement.addEventListener("touchend", this.handleTouchEnd, {
				passive: false,
			});
			boardElement.addEventListener("touchmove", this.handleTouchMove, {
				passive: false,
			});
		}
	},
	beforeDestroy() {
		window.removeEventListener("keyup", this.handleKeyup);
		window.removeEventListener("resize", this.computeLayoutMetrics);
		const boardElement = this.$refs.boards;
		if (boardElement) {
			boardElement.removeEventListener("touchstart", this.handleTouchStart);
			boardElement.removeEventListener("touchend", this.handleTouchEnd);
			boardElement.removeEventListener("touchmove", this.handleTouchMove);
		}
		if (this.activityInterval) clearInterval(this.activityInterval);
	},
	methods: {
		/* ---------- layout & metrics ---------- */
		computeLayoutMetrics() {
			const boardEl = this.$refs.boards;
			if (!boardEl) return;
			// read tile size from CSS (tile nodes already exist)
			const sample = boardEl.querySelector(".tile");
			if (sample) {
				this.cellWidth = Math.round(sample.offsetWidth);
				this.cellHeight = Math.round(sample.offsetHeight);
			}
			const st = window.getComputedStyle(boardEl);
			const padTop = parseFloat(st.paddingTop) || this.boardPadding;
			this.boardPadding = padTop;
			// gap is from your CSS; default to 18
			this.gap = 18;
		},

		/* ---------- create DOM tiles (once) ---------- */
		initializeBoard() {
			this.isMoving = true;
			const boardElement = this.$refs.boards;
			if (!boardElement) return;
			boardElement.innerHTML = "";
			for (let r = 0; r < this.rows; r++) {
				for (let c = 0; c < this.columns; c++) {
					const tile = document.createElement("div");
					tile.id = `${r}-${c}`;
					tile.className = "tile";
					// set absolute positioning; top/left will be updated
					tile.style.position = "absolute";
					tile.style.top = "0px";
					tile.style.left = "0px";
					boardElement.appendChild(tile);
				}
			}
			this.$emit("gameLoaded");
			this.$nextTick(() => {
				this.computeLayoutMetrics();
				this.updateBoardUI();
				setTimeout(() => (this.isMoving = false), 4000);
			});
		},

		/* ---------- movement helpers (unchanged logic) ---------- */
		filterZero(row) {
			return row.filter((n) => n !== 0);
		},
		slide(row) {
			row = this.filterZero(row);
			for (let i = 0; i < row.length - 1; i++) {
				if (row[i] === row[i + 1]) {
					row[i] *= 2;
					row[i + 1] = 0;
					this.score += row[i];
					if (this.bestTry < this.score) this.bestTry = this.score;
					if (row[i] > this.highest) this.highest = row[i];
				}
			}
			row = this.filterZero(row);
			while (row.length < this.columns) row.push(0);
			return row;
		},

		/* ---------- computeMoves (map sources -> targets) ---------- */
		computeMovesLeft(prevBoard, newBoard) {
			const moves = [];
			for (let r = 0; r < this.rows; r++) {
				const prevRow = prevBoard[r];
				const newRow = newBoard[r];
				const sources = [];
				const targets = [];
				for (let c = 0; c < this.columns; c++) {
					if (prevRow[c] !== 0) sources.push({ r, c, val: prevRow[c] });
					if (newRow[c] !== 0) targets.push(c);
				}
				let i = 0,
					j = 0;
				while (i < sources.length) {
					if (i + 1 < sources.length && sources[i].val === sources[i + 1].val) {
						const toC = targets[j] !== undefined ? targets[j] : sources[i].c;
						moves.push({
							from: { r, c: sources[i].c },
							to: { r, c: toC },
							merged: true,
						});
						moves.push({
							from: { r, c: sources[i + 1].c },
							to: { r, c: toC },
							merged: true,
						});
						i += 2;
						j++;
					} else {
						const toC = targets[j] !== undefined ? targets[j] : sources[i].c;
						moves.push({
							from: { r, c: sources[i].c },
							to: { r, c: toC },
							merged: false,
						});
						i++;
						j++;
					}
				}
			}
			return moves;
		},
		computeMovesRight(prevBoard, newBoard) {
			const moves = [];
			for (let r = 0; r < this.rows; r++) {
				const prevRow = prevBoard[r];
				const newRow = newBoard[r];
				const sources = [];
				const targets = [];
				for (let c = this.columns - 1; c >= 0; c--) {
					if (prevRow[c] !== 0) sources.push({ r, c, val: prevRow[c] });
					if (newRow[c] !== 0) targets.push(c);
				}
				let i = 0,
					j = 0;
				while (i < sources.length) {
					if (i + 1 < sources.length && sources[i].val === sources[i + 1].val) {
						const toC = targets[j] !== undefined ? targets[j] : sources[i].c;
						moves.push({
							from: { r, c: sources[i].c },
							to: { r, c: toC },
							merged: true,
						});
						moves.push({
							from: { r, c: sources[i + 1].c },
							to: { r, c: toC },
							merged: true,
						});
						i += 2;
						j++;
					} else {
						const toC = targets[j] !== undefined ? targets[j] : sources[i].c;
						moves.push({
							from: { r, c: sources[i].c },
							to: { r, c: toC },
							merged: false,
						});
						i++;
						j++;
					}
				}
			}
			return moves;
		},
		computeMovesUp(prevBoard, newBoard) {
			const moves = [];
			for (let c = 0; c < this.columns; c++) {
				const sources = [];
				const targets = [];
				for (let r = 0; r < this.rows; r++) {
					if (prevBoard[r][c] !== 0)
						sources.push({ r, c, val: prevBoard[r][c] });
					if (newBoard[r][c] !== 0) targets.push(r);
				}
				let i = 0,
					j = 0;
				while (i < sources.length) {
					if (i + 1 < sources.length && sources[i].val === sources[i + 1].val) {
						const toR = targets[j] !== undefined ? targets[j] : sources[i].r;
						moves.push({
							from: { r: sources[i].r, c },
							to: { r: toR, c },
							merged: true,
						});
						moves.push({
							from: { r: sources[i + 1].r, c },
							to: { r: toR, c },
							merged: true,
						});
						i += 2;
						j++;
					} else {
						const toR = targets[j] !== undefined ? targets[j] : sources[i].r;
						moves.push({
							from: { r: sources[i].r, c },
							to: { r: toR, c },
							merged: false,
						});
						i++;
						j++;
					}
				}
			}
			return moves;
		},
		computeMovesDown(prevBoard, newBoard) {
			const moves = [];
			for (let c = 0; c < this.columns; c++) {
				const sources = [];
				const targets = [];
				for (let r = this.rows - 1; r >= 0; r--) {
					if (prevBoard[r][c] !== 0)
						sources.push({ r, c, val: prevBoard[r][c] });
					if (newBoard[r][c] !== 0) targets.push(r);
				}
				let i = 0,
					j = 0;
				while (i < sources.length) {
					if (i + 1 < sources.length && sources[i].val === sources[i + 1].val) {
						const toR = targets[j] !== undefined ? targets[j] : sources[i].r;
						moves.push({
							from: { r: sources[i].r, c },
							to: { r: toR, c },
							merged: true,
						});
						moves.push({
							from: { r: sources[i + 1].r, c },
							to: { r: toR, c },
							merged: true,
						});
						i += 2;
						j++;
					} else {
						const toR = targets[j] !== undefined ? targets[j] : sources[i].r;
						moves.push({
							from: { r: sources[i].r, c },
							to: { r: toR, c },
							merged: false,
						});
						i++;
						j++;
					}
				}
			}
			return moves;
		},

		/* ---------- clone creation & animation (two-step) ---------- */
		createClonesFromMoves(moves) {
			// clones are created from the CURRENT DOM state (pre-update)
			const boardEl = this.$refs.boards;
			if (!boardEl) return { clones: [], layer: null, boardRect: null };

			const boardRect = boardEl.getBoundingClientRect();
			const layer = this.createAnimLayer();

			const clones = [];
			moves.forEach((mv) => {
				const srcEl = document.getElementById(`${mv.from.r}-${mv.from.c}`);
				if (!srcEl) return;
				const srcRect = srcEl.getBoundingClientRect();
				const startX = srcRect.left - boardRect.left;
				const startY = srcRect.top - boardRect.top;

				const clone = srcEl.cloneNode(true);
				clone.style.position = "absolute";
				clone.style.left = `${startX}px`;
				clone.style.top = `${startY}px`;
				clone.style.margin = "0";
				clone.style.transform = "translate(0,0)";
				clone.style.transition = `transform ${this.animDuration}ms cubic-bezier(.25,.8,.25,1)`;
				clone.style.zIndex = "9999";
				clone.style.pointerEvents = "none";
				// fix pixel-perfect size
				clone.style.width = `${srcRect.width}px`;
				clone.style.height = `${srcRect.height}px`;
				layer.appendChild(clone);

				clones.push({
					el: clone,
					toId: `${mv.to.r}-${mv.to.c}`,
					merged: mv.merged,
					fromPos: { x: startX, y: startY },
				});
			});

			return { clones, layer, boardRect };
		},

		animateClonesToTargets(clones, layer, boardRect) {
			// animate clones to target tile positions (DOM must already reflect final board)
			return new Promise((resolve) => {
				if (!clones || clones.length === 0) {
					if (layer && layer.parentNode) layer.parentNode.removeChild(layer);
					return resolve();
				}

				// allow the DOM to settle before reading target positions
				requestAnimationFrame(() => {
					clones.forEach((cl) => {
						const toEl = document.getElementById(cl.toId);
						if (!toEl) return;
						const toRect = toEl.getBoundingClientRect();
						const endX = toRect.left - boardRect.left;
						const endY = toRect.top - boardRect.top;
						const dx = endX - cl.fromPos.x;
						const dy = endY - cl.fromPos.y;
						// apply transform on next frame (ensures transition)
						requestAnimationFrame(() => {
							cl.el.style.transform = `translate(${dx}px, ${dy}px)`;
						});
					});

					// wait for all transitions to finish
					let finished = 0;
					const needed = clones.length;
					const handleOneEnd = () => {
						finished++;
						if (finished >= needed) {
							// apply merge pop to target elements
							const mergedTargets = new Set();
							clones.forEach((c) => {
								if (c.merged) mergedTargets.add(c.toId);
							});
							mergedTargets.forEach((id) => {
								const el = document.getElementById(id);
								if (el) {
									el.classList.add("merge-pop");
									setTimeout(
										() => el.classList.remove("merge-pop"),
										this.animDuration + 30
									);
								}
							});

							// cleanup clones & layer
							clones.forEach((c) => {
								if (c.el && c.el.parentNode) c.el.parentNode.removeChild(c.el);
							});
							if (layer && layer.parentNode)
								layer.parentNode.removeChild(layer);
							resolve();
						}
					};

					clones.forEach((c) => {
						let handled = false;
						const onEnd = (ev) => {
							if (ev.propertyName === "transform" && !handled) {
								handled = true;
								c.el.removeEventListener("transitionend", onEnd);
								handleOneEnd();
							}
						};
						c.el.addEventListener("transitionend", onEnd);
						// fallback timeout
						setTimeout(() => {
							if (!handled) {
								handled = true;
								c.el.removeEventListener("transitionend", onEnd);
								handleOneEnd();
							}
						}, this.animDuration + 80);
					});
				});
			});
		},

		// helper that ensures anim-layer exists (used by createClonesFromMoves)
		createAnimLayer() {
			const boardEl = this.$refs.boards;
			if (!boardEl) return null;
			let layer = boardEl.querySelector(".anim-layer");
			if (!layer) {
				layer = document.createElement("div");
				layer.className = "anim-layer";
				layer.style.position = "absolute";
				layer.style.left = "0";
				layer.style.top = "0";
				layer.style.width = "100%";
				layer.style.height = "100%";
				layer.style.pointerEvents = "none";
				boardEl.appendChild(layer);
			}
			return layer;
		},

		/* ---------- board DOM update (numbers & pixel position) ---------- */
		updateBoardUI() {
			const boardEl = this.$refs.boards;
			if (!boardEl) return;
			this.computeLayoutMetrics();
			const isMobile = window.innerWidth < 1028;
			const gap = isMobile ? 14.5 : this.gap;
			for (let r = 0; r < this.rows; r++) {
				for (let c = 0; c < this.columns; c++) {
					const id = `${r}-${c}`;
					const tile = document.getElementById(id);
					if (!tile) continue;
					const val = this.board[r][c];
					tile.className = "tile"; // reset classes
					if (val > 0) tile.classList.add(`x${val}`);
					tile.innerText = val > 0 ? String(val) : "";

					// set top/left instantly (clones cover movement)
					const top = this.boardPadding + r * (this.cellHeight + gap);
					const left = this.boardPadding + c * (this.cellWidth + gap);

					tile.style.transition = "none"; // no transition for set
					tile.style.top = `${top}px`;
					tile.style.left = `${left}px`;
				}
			}
		},

		/* ---------- slide functions: create clones, update DOM, animate ---------- */
		async slideLeft() {
			if (this.isMoving) return false;
			this.isMoving = true;
			const prevBoard = JSON.parse(JSON.stringify(this.board));
			const prevScore = this.score;
			const prevHighest = this.highest;

			// compute new board
			const newBoard = [];
			for (let r = 0; r < this.rows; r++) {
				newBoard.push(this.slide([...this.board[r]]));
			}

			if (JSON.stringify(prevBoard) === JSON.stringify(newBoard)) {
				this.isMoving = false;
				return false;
			}

			const moves = this.computeMovesLeft(prevBoard, newBoard);
			// clone from prev DOM
			const { clones, layer, boardRect } = this.createClonesFromMoves(moves);

			// update logical board & DOM
			this.board = newBoard;
			this.updateBoardUI();

			// save history
			this.boardHistory.push(prevBoard);
			this.scoreHistory.push(prevScore);
			this.highestHistory.push(prevHighest);
			this.canUndo = true;
			if (this.boardHistory.length > 10) {
				this.boardHistory.shift();
				this.scoreHistory.shift();
				this.highestHistory.shift();
			}

			// animate clones to new positions
			await this.animateClonesToTargets(clones, layer, boardRect);

			this.isMoving = false;
			return true;
		},

		async slideRight() {
			if (this.isMoving) return false;
			this.isMoving = true;
			const prevBoard = JSON.parse(JSON.stringify(this.board));
			const prevScore = this.score;
			const prevHighest = this.highest;

			const newBoard = [];
			for (let r = 0; r < this.rows; r++) {
				newBoard.push(this.slide([...this.board[r]].reverse()).reverse());
			}
			if (JSON.stringify(prevBoard) === JSON.stringify(newBoard)) {
				this.isMoving = false;
				return false;
			}
			const moves = this.computeMovesRight(prevBoard, newBoard);
			const { clones, layer, boardRect } = this.createClonesFromMoves(moves);

			this.board = newBoard;
			this.updateBoardUI();

			this.boardHistory.push(prevBoard);
			this.scoreHistory.push(prevScore);
			this.highestHistory.push(prevHighest);
			this.canUndo = true;
			if (this.boardHistory.length > 10) {
				this.boardHistory.shift();
				this.scoreHistory.shift();
				this.highestHistory.shift();
			}

			await this.animateClonesToTargets(clones, layer, boardRect);

			this.isMoving = false;
			return true;
		},

		async slideUp() {
			if (this.isMoving) return false;
			this.isMoving = true;
			const prevBoard = JSON.parse(JSON.stringify(this.board));
			const prevScore = this.score;
			const prevHighest = this.highest;

			const newBoard = Array.from({ length: this.rows }, () =>
				Array(this.columns).fill(0)
			);
			for (let c = 0; c < this.columns; c++) {
				const col = [
					this.board[0][c],
					this.board[1][c],
					this.board[2][c],
					this.board[3][c],
				];
				const newCol = this.slide([...col]);
				for (let r = 0; r < this.rows; r++) newBoard[r][c] = newCol[r];
			}

			if (JSON.stringify(prevBoard) === JSON.stringify(newBoard)) {
				this.isMoving = false;
				return false;
			}

			const moves = this.computeMovesUp(prevBoard, newBoard);
			const { clones, layer, boardRect } = this.createClonesFromMoves(moves);

			this.board = newBoard;
			this.updateBoardUI();

			this.boardHistory.push(prevBoard);
			this.scoreHistory.push(prevScore);
			this.highestHistory.push(prevHighest);
			this.canUndo = true;
			if (this.boardHistory.length > 10) {
				this.boardHistory.shift();
				this.scoreHistory.shift();
				this.highestHistory.shift();
			}

			await this.animateClonesToTargets(clones, layer, boardRect);

			this.isMoving = false;
			return true;
		},

		async slideDown() {
			if (this.isMoving) return false;
			this.isMoving = true;
			const prevBoard = JSON.parse(JSON.stringify(this.board));
			const prevScore = this.score;
			const prevHighest = this.highest;

			const newBoard = Array.from({ length: this.rows }, () =>
				Array(this.columns).fill(0)
			);
			for (let c = 0; c < this.columns; c++) {
				const col = [
					this.board[0][c],
					this.board[1][c],
					this.board[2][c],
					this.board[3][c],
				].reverse();
				const newCol = this.slide([...col]).reverse();
				for (let r = 0; r < this.rows; r++) newBoard[r][c] = newCol[r];
			}

			if (JSON.stringify(prevBoard) === JSON.stringify(newBoard)) {
				this.isMoving = false;
				return false;
			}

			const moves = this.computeMovesDown(prevBoard, newBoard);
			const { clones, layer, boardRect } = this.createClonesFromMoves(moves);

			this.board = newBoard;
			this.updateBoardUI();

			this.boardHistory.push(prevBoard);
			this.scoreHistory.push(prevScore);
			this.highestHistory.push(prevHighest);
			this.canUndo = true;
			if (this.boardHistory.length > 10) {
				this.boardHistory.shift();
				this.scoreHistory.shift();
				this.highestHistory.shift();
			}

			await this.animateClonesToTargets(clones, layer, boardRect);

			this.isMoving = false;
			return true;
		},

		/* ---------- spawn + helpers ---------- */
		setTwo(tileTag) {
			// find empty spots
			const empties = [];
			for (let r = 0; r < this.rows; r++) {
				for (let c = 0; c < this.columns; c++) {
					if (this.board[r][c] === 0) empties.push({ r, c });
				}
			}
			if (empties.length === 0) return;
			const { r, c } = empties[Math.floor(Math.random() * empties.length)];
			this.board[r][c] = 2;
			this.saveGame();

			this.$nextTick(() => {
				// update DOM & do pop animation
				this.updateBoardUI();
				const tile = document.getElementById(`${r}-${c}`);
				if (tile) {
					tile.classList.add("new");
					setTimeout(() => tile.classList.remove("new"), 220);
				}
			});
		},

		/* ---------- input / touch ---------- */
		handleTouchStart(e) {
			this.touchStartX = e.touches[0].clientX;
			this.touchStartY = e.touches[0].clientY;
		},
		handleTouchMove(e) {
			const currentY = e.touches[0].clientY;
			const currentX = e.touches[0].clientX;
			const deltaY = currentY - this.touchStartY;
			const deltaX = currentX - this.touchStartX;
			if (Math.abs(deltaY) > Math.abs(deltaX)) e.preventDefault();
		},
		async handleTouchEnd(e) {
			if (this.isMoving) return;
			const touchEndX = e.changedTouches[0].clientX;
			const touchEndY = e.changedTouches[0].clientY;
			const deltaX = touchEndX - this.touchStartX;
			const deltaY = touchEndY - this.touchStartY;
			const absDeltaX = Math.abs(deltaX);
			const absDeltaY = Math.abs(deltaY);

			let moved = false;
			if (absDeltaX > absDeltaY) {
				if (deltaX > 0) moved = await this.slideRight();
				else moved = await this.slideLeft();
			} else {
				if (deltaY > 0) moved = await this.slideDown();
				else moved = await this.slideUp();
			}
			if (moved) this.setTwo();

			// check game over
			if (!this.hasEmptyTile() && !this.hasValidMove() && !this.gameOver) {
				this.gameOver = true;
				await this.submitScore("2048", this.score);
				this.deleteGameState("2048");
				this.handleGameOver();
			}
		},

		async handleKeyup(e) {
			if (this.isMoving) return;
			let moved = false;
			if (e.code === "ArrowLeft") moved = await this.slideLeft();
			else if (e.code === "ArrowRight") moved = await this.slideRight();
			else if (e.code === "ArrowUp") moved = await this.slideUp();
			else if (e.code === "ArrowDown") moved = await this.slideDown();

			if (moved) {
				this.setTwo();
				if (!this.hasEmptyTile() && !this.hasValidMove()) {
					this.gameOver = true;
					await this.submitScore("2048", this.score);
					this.handleGameOver();
				}
			}
		},

		/* ---------- undo / saving / loading ---------- */
		undoMove() {
			if (
				!this.canUndo ||
				this.boardHistory.length === 0 ||
				this.undoCount >= 5
			)
				return;
			const previousBoard = this.boardHistory.pop();
			const previousScore = this.scoreHistory.pop();
			const previousHighest = this.highestHistory.pop();
			this.board = previousBoard;
			this.score = previousScore;
			this.highest = previousHighest;
			this.updateBoardUI();
			this.canUndo = this.boardHistory.length > 0;
			this.gameOver = false;
			this.undoCount++;
			this.saveGame();
		},

		startActivityTimer() {
			this.activityInterval = setInterval(async () => {
				await saveActivity("2048");
			}, 30000);
		},

		handleGameOver() {
			const gameWon = this.highest >= 2048;
			this.$emit("gameOver", gameWon, this.score);
		},

		setGame() {
			this.board = Array(4)
				.fill(null)
				.map(() => Array(4).fill(0));
			this.setTwo("first");
			this.setTwo("second");
			this.$nextTick(() => {
				this.computeLayoutMetrics();
				this.updateBoardUI();
			});
		},

		async resetGame() {
			this.buttonLoader = true;
			this.boardHistory = [];
			this.scoreHistory = [];
			this.highestHistory = [];
			this.undoCount = 0;
			this.canUndo = false;
			await this.deleteGameState("2048");
			this.score = 0;
			this.setGame();
			await this.getMyRank();
			this.showReset = false;
			this.buttonLoader = false;
		},

		saveGame() {
			const gameState = {
				gameOver: this.gameOver,
				game_data: this.board,
				boardHistory: this.boardHistory,
				score: this.score,
				gameWon: this.highest > 2048,
				undoCount: this.undoCount,
				scoreHistory: this.scoreHistory,
			};
			this.saveGameState("2048", gameState);
		},

		async loadGameState() {
			try {
				const savedState = await getGameState("2048");
				if (
					savedState &&
					savedState.data &&
					savedState.data.data &&
					savedState.data.data.state &&
					savedState.data.data.state.game_data &&
					savedState.data.data.state.game_data.length > 0 &&
					savedState.status === 200
				) {
					const { game_data, score, boardHistory, undoCount, scoreHistory } =
						savedState.data.data.state;
					this.board = game_data;
					this.score = score || 0;
					this.boardHistory = boardHistory || [];
					this.undoCount = undoCount || 0;
					this.scoreHistory = scoreHistory || [];
					this.canUndo = this.boardHistory.length > 0 && this.undoCount <= 5;
					this.$nextTick(() => {
						this.computeLayoutMetrics();
						this.updateBoardUI();
					});
				} else {
					this.setGame();
				}
			} catch (err) {
				this.setGame();
			}
		},

		async getMyRank() {
			const res = await getMyRank("2048");
			if (res && res.status === 200) {
				this.myScore = res.data.data.current_score;
				const all_scores = res.data.data.all_scores || [];
				const scores = all_scores.map((entry) => entry.score);
				const maxScore = scores.length ? Math.max(...scores) : 0;
				this.bestTry = Math.max(maxScore, this.bestTry || 0);
				this.totalPlayed = all_scores.length;
			}
		},

		hasEmptyTile() {
			for (let r = 0; r < this.rows; r++) {
				for (let c = 0; c < this.columns; c++) {
					if (this.board[r][c] === 0) return true;
				}
			}
			return false;
		},

		hasValidMove() {
			for (let r = 0; r < this.rows; r++) {
				for (let c = 0; c < this.columns; c++) {
					if (this.board[r][c] === 0) return true;
					if (c < this.columns - 1 && this.board[r][c] === this.board[r][c + 1])
						return true;
					if (r < this.rows - 1 && this.board[r][c] === this.board[r + 1][c])
						return true;
				}
			}
			return false;
		},
	},
};
</script>

<style>
/* Add some basic styling */

.twozero {
	display: flex;
	flex-direction: column;
	align-items: center;
	position: relative;
}

.twozero #title {
	font-size: 32px;
	font-weight: 900;
	font-family: "Lato", sans-serif;
	color: var(--bg-proTheme);
}

.twozero article {
	backdrop-filter: blur(4px);
	background-color: #d9e4f5;
	border-radius: 8px;
	padding: 10px;
	width: 120px;
	height: 75px;
	text-align: center;
}

.twozero article h5 {
	font-family: "Lato", sans-serif;
	font-size: var(--sixe-14);
	font-weight: var(--weight-LM);
	color: #595959;
}

.twozero article h4 {
	font-family: Lato;
	font-size: var(--sixe-20);
	font-weight: 900;
	color: var(--bg-proTheme);
	margin-bottom: 0;
}

#boards {
	width: 440px;
	height: 418px;
	padding: 18px;
	background-color: #bdad9f;
	border-radius: 5px;
	border: none;
	gap: 18px;
	/* moved away from flex to absolute layout */
	margin-top: 20px;
	position: relative;
}

#boards .tile {
	width: 87px;
	height: 82px;
	border: none;
	border-radius: 4px;
	font-size: 36px;
	font-weight: 800;
	font-family: "Lato", sans-serif;
	color: #ffffff;
	display: flex;
	justify-content: center;
	align-items: center;
	box-shadow: 0px 3.62px 3.62px 0px #00000040;
	background-color: #cfc1b3;
	position: absolute; /* absolute for pixel positions */
	transition: transform 150ms ease-in-out;
}

/* new tile pop */
.tile.new {
	animation: pop-in 150ms ease-out;
}

/* merge pop visual */
.tile.merge-pop {
	animation: pop-merge 160ms ease-out;
}

@keyframes pop-in {
	from {
		transform: scale(0);
	}
	to {
		transform: scale(1);
	}
}

@keyframes pop-merge {
	from {
		transform: scale(1.2);
	}
	to {
		transform: scale(1);
	}
}
.twozero-margin {
	margin-top: 20px;
	margin-bottom: 40px;
}

/* colored tiles */

#boards .tile.x2 {
	background-color: #eee4da;
	color: #000;
}
#boards .tile.x4 {
	background-color: #ede0c8;
	color: #000;
}
#boards .tile.x8 {
	background-color: #f2b179;
}
#boards .tile.x16 {
	background-color: #f59563;
}
#boards .tile.x32 {
	background-color: #f67c5f;
}
#boards .tile.x64 {
	background-color: #f65e3b;
}
#boards .tile.x128 {
	background-color: #edcf72;
}
#boards .tile.x256 {
	background-color: #edcc61;
}
#boards .tile.x512 {
	background-color: #edc850;
}
#boards .tile.x1024 {
	background-color: #edc53f;
}
#boards .tile.x2048 {
	background-color: #edc22e;
}
#boards .tile.x4096 {
	background-color: #9ab0ac;
	color: #fff;
}
#boards .tile.x8192 {
	background-color: #3c3a32;
	color: #fff;
}
.reset-twozero-hover {
	background: #00bfae59;
	font-family: "Montserrat", sans-serif;
	font-size: 16px;
	font-weight: 700;
	text-align: center;
	color: #3b3b3b;
	border-radius: 21px;
	height: fit-content;
	position: absolute;
	right: calc((100% - 557px) / 2);
	top: 315px;
}

.reset-twozero-hover img {
	position: relative;
	z-index: 2;
	transition: all 0.3s ease; /* Added transition */
	cursor: pointer;
}

.reset-twozero-hover span {
	position: absolute;
	background: #00bfae59;
	height: 42px;
	display: flex;
	align-items: center;
	border-radius: 20px;
	padding-left: 50px;
	border: 1px solid #8c8c8c54;
	z-index: 1;
	left: -8px;
	top: 0px;
	opacity: 0;
	visibility: hidden;
	white-space: nowrap;
	transition: all 0.3s ease; /* Added transition */
}

/* Show span on hover of img */
.reset-twozero-hover:hover span {
	opacity: 1;
	visibility: visible;
	left: 0px; /* Adjust position if needed */
}

@media (max-width: 1028px) {
	#boards {
		width: 345px;
		height: 338px;
		padding: 10px;
		gap: 10px;
		margin-bottom: 82px;
	}
	#boards .tile {
		font-size: 24px;
		width: 71px;
		height: 67px;
	}
	.twozero-margin {
		margin-top: 11px;
		margin-bottom: 40px;
	}
	.reset-twozero-hover {
		top: 470px;
		bottom: unset;
		right: unset;
		width: fit-content;
	}
	.reset-twozero-hover span {
		left: -9px;
		top: 0px;
		position: unset;
		opacity: 1;
		visibility: visible;
		display: inline-flex;
		padding-left: 5px;
		height: 32px;
		background: none;
		border: none;
		margin-left: 0 !important;
	}
	.reset-twozero-hover img {
		height: 32px;
		width: 32px;
	}
	.twozero article {
		width: 150px;
	}
}
.anim-layer {
	position: absolute;
	inset: 0;
	pointer-events: none;
	z-index: 50;
}

/* merge pop visual (used temporarily after animation) */
.tile.merge-pop {
	animation: pop-merge 160ms ease-out;
}
</style>
